<template>
    <Modal :show="visible" width="large" @update:show="cancel">
        <h3 class="text-base font-semibold mb-4 text-ui-active">Save Design</h3>

        <div class="mb-3">
            <label class="block text-xs font-medium text-gray-400 mb-1">Design name</label>
            <input v-model="localFilename"
                class="w-full p-2 border border-white/20 rounded bg-white/15 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-ui-active"
                placeholder="Design name" />
        </div>

        <div class="mb-3 flex items-center gap-4 flex-wrap">
            <label class="text-xs font-medium text-gray-400">Version</label>
            <div class="flex items-center gap-2">
                <span class="text-xs text-gray-500">Major</span>
                <input v-model.number="localMajor" type="number" min="0"
                    class="w-16 p-2 border border-white/20 rounded text-center bg-white/15 text-white text-xs focus:outline-none focus:border-ui-active"
                    @change="onMajorChange" />
                <span class="text-xs text-gray-500">Minor</span>
                <input v-model.number="localMinor" type="number" min="0"
                    class="w-16 p-2 border border-white/20 rounded text-center bg-white/15 text-white text-xs focus:outline-none focus:border-ui-active" />
            </div>
            <span class="text-xs text-gray-500">
                saves as <span class="font-mono bg-white/15 border border-white/20 px-1.5 py-0.5 rounded text-ui-active">{{ localFilename }}_v{{ localMajor }}.{{ localMinor }}.zip</span>
            </span>
        </div>

        <div class="mb-3 w-full">
            <label class="block text-xs font-medium text-gray-400 mb-1">Comment</label>
            <textarea v-model="comment" rows="2"
                class="w-full p-2 border border-white/20 rounded resize-none bg-white/15 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-ui-active"
                placeholder="Describe the changes in this version..." />
        </div>

        <div v-if="versionHistory.length > 0" class="mb-3">
            <label class="block text-xs font-medium text-gray-400 mb-1">Version history</label>
            <div class="max-h-44 overflow-y-auto border border-white/20 rounded">
                <table class="w-full text-xs">
                    <thead class="bg-ui-bg sticky top-0">
                        <tr>
                            <th class="text-left px-3 py-1.5 font-medium text-gray-400">Version</th>
                            <th class="text-left px-3 py-1.5 font-medium text-gray-400">Date</th>
                            <th class="text-left px-3 py-1.5 font-medium text-gray-400">File</th>
                            <th class="text-left px-3 py-1.5 font-medium text-gray-400">Comment</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="entry in reversedHistory" :key="entry.name"
                            class="border-t border-white/20 hover:bg-white/10">
                            <td class="px-3 py-1.5 font-mono text-ui-active">{{ formatVersion(entry) }}</td>
                            <td class="px-3 py-1.5 text-gray-400 whitespace-nowrap">{{ formatDate(entry.date) }}</td>
                            <td class="px-3 py-1.5 text-gray-500 font-mono">{{ entry.name }}</td>
                            <td class="px-3 py-1.5 text-gray-400">{{ entry.comment || '' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="flex justify-end gap-2 mt-4">
            <form-button class="w-24" color="gray" @click="cancel">Cancel</form-button>
            <form-button class="w-24" color="success" @click="save">Save</form-button>
        </div>
    </Modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Modal from '../Modal.vue';

const props = defineProps({
    visible: Boolean,
    filenamePreset: { type: String, default: '' },
    currentMajor: { type: Number, default: 0 },
    currentMinor: { type: Number, default: 0 },
    versionHistory: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:visible', 'save']);

const localFilename = ref(props.filenamePreset);
const localMajor = ref(props.currentMajor);
const localMinor = ref(props.currentMinor + 1);
const comment = ref('');

watch(() => props.filenamePreset, (val) => { localFilename.value = val; });
watch(() => props.currentMajor, (val) => { localMajor.value = val; });
watch(() => props.currentMinor, (val) => { localMinor.value = val + 1; });

const reversedHistory = computed(() => [...props.versionHistory].reverse());

function onMajorChange() {
    localMinor.value = 0;
}

function formatVersion(entry) {
    if (entry.major !== undefined) return `v${entry.major}.${entry.minor}`;
    return `v${entry.version}`;
}

function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString();
}

function cancel() {
    emit('update:visible', false);
}

function save() {
    emit('save', {
        filename: localFilename.value,
        major: localMajor.value,
        minor: localMinor.value,
        comment: comment.value,
    });
    comment.value = '';
    emit('update:visible', false);
}
</script>
